import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge,
  Chip,
  Divider,
  Button,
  Paper,
  Fade,
  Slide,
} from '@mui/material';
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { useSocket } from '../../contexts/SocketContext';

const NotificationPanel = ({ open, onClose }) => {
  const { notifications, removeNotification, clearNotifications } = useSocket();

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <SuccessIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'info':
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success':
        return '#4CAF50';
      case 'error':
        return '#F44336';
      case 'warning':
        return '#FF9800';
      case 'info':
      default:
        return '#2196F3';
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          maxWidth: '90vw',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
            color: '#000000',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NotificationsIcon sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Benachrichtigungen
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={unreadCount}
                size="small"
                sx={{
                  ml: 1,
                  backgroundColor: '#000000',
                  color: '#FFFFFF',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
          <Box>
            {notifications.length > 0 && (
              <IconButton
                onClick={clearNotifications}
                size="small"
                sx={{ mr: 1, color: '#000000' }}
              >
                <DeleteIcon />
              </IconButton>
            )}
            <IconButton onClick={onClose} size="small" sx={{ color: '#000000' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Notifications List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 3,
                textAlign: 'center',
              }}
            >
              <NotificationsIcon
                sx={{
                  fontSize: 64,
                  color: '#e0e0e0',
                  mb: 2,
                }}
              />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Keine Benachrichtigungen
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sie haben derzeit keine neuen Benachrichtigungen
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification, index) => (
                <Fade
                  key={notification.id}
                  in
                  timeout={300}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      m: 1,
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <ListItem
                      sx={{
                        alignItems: 'flex-start',
                        py: 2,
                        px: 2,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                        {getNotificationIcon(notification.type)}
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            {notification.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              {notification.message}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDistanceToNow(notification.timestamp, {
                                  addSuffix: true,
                                  locale: de,
                                })}
                              </Typography>
                              
                              <Chip
                                label={notification.type}
                                size="small"
                                sx={{
                                  backgroundColor: getNotificationColor(notification.type),
                                  color: '#FFFFFF',
                                  fontSize: '0.7rem',
                                  height: 20,
                                }}
                              />
                            </Box>
                          </Box>
                        }
                      />
                      
                      <IconButton
                        onClick={() => removeNotification(notification.id)}
                        size="small"
                        sx={{
                          ml: 1,
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'error.main',
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  </Paper>
                </Fade>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        {notifications.length > 0 && (
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa',
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              sx={{
                borderColor: '#FFD700',
                color: '#FFD700',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#F57F17',
                  backgroundColor: 'rgba(255, 215, 0, 0.1)',
                },
              }}
            >
              Aktualisieren
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default NotificationPanel;
